
import React from 'react';
import { PersonalityType } from '../types';
import { PERSONALITY_CONFIG } from '../constants';
import { CheckCircle2, ArrowLeft } from 'lucide-react';

interface Props {
  onSelect: (type: PersonalityType) => void;
  selected: PersonalityType | null;
  onConfirm: () => void;
  t: any;
}

const PersonalitySelector: React.FC<Props> = ({ onSelect, selected, onConfirm, t }) => {
  return (
    <div className="flex flex-col items-center justify-center min-h-full p-6 animate-fade-in bg-slate-50">
      <div className="text-center mb-10">
        <h1 className="text-3xl font-black text-slate-800 mb-2">{t.personality_title}</h1>
        <p className="text-slate-500 max-w-md mx-auto">
          {t.personality_subtitle}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full max-w-4xl mb-10">
        {Object.values(PERSONALITY_CONFIG).map((persona: any) => {
          const isSelected = selected === persona.id;
          return (
            <button
              key={persona.id}
              onClick={() => onSelect(persona.id)}
              className={`relative group flex items-start p-6 rounded-3xl border-2 transition-all duration-300 text-right
                ${isSelected 
                  ? `${persona.border} ${persona.bg} shadow-xl scale-[1.02]` 
                  : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-md'
                }
              `}
            >
              <div className={`ml-4 p-3 rounded-2xl bg-white/80 backdrop-blur-sm ${persona.color} shadow-sm group-hover:scale-110 transition-transform`}>
                {persona.icon}
              </div>
              <div className="flex-1">
                <h3 className={`font-black text-lg mb-1 ${isSelected ? 'text-slate-900' : 'text-slate-700'}`}>
                  {t[persona.labelKey]}
                </h3>
                <p className="text-sm font-bold text-slate-400 mb-2">{t[persona.descKey]}</p>
                <p className="text-xs text-slate-500 leading-relaxed opacity-90">{t[persona.detailsKey]}</p>
              </div>
              {isSelected && (
                <div className="absolute top-4 left-4 text-emerald-500">
                  <CheckCircle2 className="w-6 h-6" />
                </div>
              )}
            </button>
          );
        })}
      </div>

      <button
        onClick={onConfirm}
        disabled={!selected}
        className={`px-12 py-4 rounded-full text-lg font-black shadow-xl transition-all duration-300 flex items-center gap-2
          ${selected 
            ? 'bg-indigo-600 text-white hover:bg-indigo-700 hover:shadow-indigo-500/30 translate-y-0' 
            : 'bg-slate-200 text-slate-400 cursor-not-allowed translate-y-2 opacity-50'
          }
        `}
      >
        {t.start_journey}
        {selected && <ArrowLeft className="w-5 h-5 rtl:rotate-180" />}
      </button>
    </div>
  );
};

export default PersonalitySelector;
